import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { WebStorageService } from 'src/app/shared/services/web-storage.service';
import { DateFormatModel } from 'src/app/shared/models/common/date-format-model';
import { SalesOrderResponseModel } from 'src/app/shared/models/response/sales-order-response';
import { PatientBookAppointmentRequest } from 'src/app/shared/models/request/patient-book-appointment-request-model';
import { takeUntil } from 'rxjs/operators';
import { fromEvent, Subject } from 'rxjs';
import { PaymentInformationModel } from 'src/app/interfaces/payment-information';
import { PaymentService } from 'src/app/shared/services/payment.service';
import { ResultOfT } from 'src/app/shared/models/response/result';
import { AppointmentRequestModel } from 'src/app/shared/models/request/appointment-request';
import { PatientPaymentMode } from 'src/app/interfaces/payment-enums';

@Component({
  selector: 'app-patient-appointment-booking',
  templateUrl: './patient-appointment-booking.component.html',
  styleUrls: ['./patient-appointment-booking.component.sass'],
  standalone: false
})
export class PatientAppointmentBookingComponent implements OnInit {
  private unsubscriber = new Subject<boolean>();
  patientName: string = '';
  appDateFormat: DateFormatModel;
  value: number = 16;
  patientBookingTypeId: string = '';
  salesOrderData: SalesOrderResponseModel;
  appointmentTitle: string;
  appointmentSelectLabel: string;
  activeStepNo: number = 1;
  patientBookAppointmentRequest: PatientBookAppointmentRequest;
  @Input() isAppoitmentBooked: boolean = false;

  isFacilityOrTherapistSelectionChanged: boolean = false;
  paymentData: PaymentInformationModel;
  isPaymentApplicable = false;
  paymentStepName = 'Pay & Book Appointment';

  appointmentRequest: AppointmentRequestModel;

  private processResult<T>(result: ResultOfT<T>, onSuccess: () => void, onError?: () => void): void {
    if (result && result.isSuccess) {
      onSuccess();
    } else if (onError) {
      onError();
    }
  }

  constructor(private router: Router, private _cdr: ChangeDetectorRef, private webStorageService: WebStorageService, private paymentService: PaymentService) {
    this.appDateFormat = this.webStorageService.getDateFormat();
    this.patientBookingTypeId = this.webStorageService.getPatientBookingTypeId();

    this.salesOrderData = this.webStorageService.getSalesOrderData();
    this.patientName = this.salesOrderData?.patientInfo?.crmPatientFirstName + ' ' + this.salesOrderData?.patientInfo?.crmPatientLastName;

    let onlineAppointmentTypeResponseModel = this.webStorageService.getOnlineAppointmentTypeNames();

    if (onlineAppointmentTypeResponseModel?.length > 0) {
      if (this.patientBookingTypeId == 'R') {
        let RemoteSetup = onlineAppointmentTypeResponseModel.find((x) => x.crmOnlineAppointmentType == 'RemoteSetup');
        if (RemoteSetup) {
          this.appointmentTitle = RemoteSetup.crmOnlineAppointmentTitle;
          this.appointmentSelectLabel = 'Select Remote';
        }
      } else if (this.patientBookingTypeId == 'T') {
        let TherapistVisit = onlineAppointmentTypeResponseModel.find((x) => x.crmOnlineAppointmentType == 'TherapistVisit');
        if (TherapistVisit) {
          this.appointmentTitle = TherapistVisit.crmOnlineAppointmentTitle;
          this.appointmentSelectLabel = 'Select Therapist';
        }
      } else if (this.patientBookingTypeId == 'SG') {
        let SelfGuided = onlineAppointmentTypeResponseModel.find((x) => x.crmOnlineAppointmentType == 'SelfGuided');
        if (SelfGuided) {
          this.appointmentTitle = SelfGuided.crmOnlineAppointmentTitle;
          this.appointmentSelectLabel = 'Self Guided';
        }
      } else if (this.patientBookingTypeId == 'F') {
        let FacilityVisit = onlineAppointmentTypeResponseModel.find((x) => x.crmOnlineAppointmentType == 'FacilityVisit');
        if (FacilityVisit) {
          this.appointmentTitle = FacilityVisit.crmOnlineAppointmentTitle;
          this.appointmentSelectLabel = 'Select Clinic';
        }
      }
    }
    this.activeStepNo = 1;

    this.paymentService.getPaymentInformation(this.salesOrderData.crmSalesorderId).subscribe((result) => {
      this.paymentData = result.responseModel;
      this.setPaymentFlag();
      this.setPaymentStepName();
    });
  }

  private setPaymentFlag(): void {
    const p = this.paymentData;

    // Default to false
    this.isPaymentApplicable = false;

    // Global eligibility checks
    if (!p.enabled || p.isExcluded || p.salesOrderStatus === 'void' || p.patientPaymentMode === PatientPaymentMode.NO_PAYMENT) {
      this.webStorageService.setIsPaymentApplicable(false);
      return;
    }

    switch (p.patientPaymentMode) {
      case PatientPaymentMode.COLLECT_PAYMENT:
        this.isPaymentApplicable = p.paymentAmount > 0;
        break;

      case PatientPaymentMode.COLLECT_AND_SAVE_CARD:
        this.isPaymentApplicable = p.paymentAmount > 0 || !p.paymentCard;
        break;

      case PatientPaymentMode.SAVE_CARD:
        this.isPaymentApplicable = !p.paymentCard;
        break;

      default:
        this.isPaymentApplicable = true;
    }

    this.webStorageService.setIsPaymentApplicable(this.isPaymentApplicable);
  }

  private setPaymentStepName() {
    const mode = this.paymentData.patientPaymentMode;
    if (mode === PatientPaymentMode.COLLECT_AND_SAVE_CARD) {
      this.paymentStepName = 'Pay & Book Appointment';
    } else if (mode === PatientPaymentMode.SAVE_CARD) {
      this.paymentStepName = 'Authorize & Book Appointment';
    } else {
      this.paymentStepName = 'Pay & Book Appointment';
    }

    this.webStorageService.setPaymentStepName(this.paymentStepName);
  }

  ngOnInit(): void {
    history.pushState(null, '');

    fromEvent(window, 'popstate')
      .pipe(takeUntil(this.unsubscriber))
      .subscribe((_) => {
        history.pushState(null, '');
      });
  }

  onBack() {
    const paymentSetup = this.webStorageService.getPaymentSetup();
    if (paymentSetup) {
      this.paymentService.clearPaymentSetup(paymentSetup).subscribe(() => {
        this.webStorageService.deletePaymentSetup();
      });
    }

    let url = this.webStorageService.getPatientBookingUrl().split('?')[0].split('home/')[1];
    if (this.salesOrderData.schAppointmentId == 0) this.router.navigate(['patient/home/' + url]);
    else this.router.navigate(['/patient/home/' + url], { queryParams: { utm_source: 'rescheduleappointment' } });
  }

  onNext() {}

  setActiveStepNo(stepNo: number) {
    this.activeStepNo = stepNo;
  }

  setAppoitmentBookRequest(data: PatientBookAppointmentRequest) {
    this.patientBookAppointmentRequest = data;
  }

  GetFacilityOrTherapistSlectionChangeRequest(data) {
    this.isFacilityOrTherapistSelectionChanged = data;
    this._cdr.detectChanges();
  }

  onProceedToPayment(appointmentRequest: AppointmentRequestModel) {
    this.activeStepNo = 4;
    this.appointmentRequest = appointmentRequest;
  }
}
